import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";

/* ------------------------------------------------------------------ *
 * A small walking figure that paces back and forth at a fixed point
 * on a route line (About's ElevationProfile, Process's route bar).
 * Positioned via leftPct/topPct (0-100), matching the fractional
 * control-point coordinates already used by RouteWaypoints — the two
 * components share the same coordinate space without RouteHiker
 * needing to know anything about the curve math.
 * ------------------------------------------------------------------ */

// Filled tapered quad between two joints — reads as a real limb instead of
// a uniform-width stroke. `w0`/`w1` are widths at the start/end joint.
function limb(ctx, x0, y0, x1, y1, w0, w1, color) {
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  ctx.beginPath();
  ctx.moveTo(x0 + nx * (w0 / 2), y0 + ny * (w0 / 2));
  ctx.lineTo(x1 + nx * (w1 / 2), y1 + ny * (w1 / 2));
  ctx.lineTo(x1 - nx * (w1 / 2), y1 - ny * (w1 / 2));
  ctx.lineTo(x0 - nx * (w0 / 2), y0 - ny * (w0 / 2));
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  // rounded joint at the far end hides the seam with the next segment
  ctx.beginPath();
  ctx.arc(x1, y1, w1 / 2, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Gait timing grounded in standard walk-cycle references (contact / down /
 * passing / up poses): the body is at its LOWEST at contact (legs at max
 * stride, sin(phase) at its extreme) and TALLEST at passing (legs crossing
 * under the body, sin(phase)=0) — so vertical bob must peak on cos(phase),
 * not sin(phase). Arms swing contralateral to legs (opposite leg/arm pair
 * forward together), matching real bipedal balance mechanics.
 */
function drawHiker(ctx, h, { phase, reduced }) {
  const s = h / 60; // normalize to a 60-unit-tall figure
  ctx.save();
  ctx.scale(s, s);

  const bob = reduced ? 0 : Math.abs(Math.cos(phase)) * 1.8;
  ctx.translate(0, -bob);

  const legSwing = reduced ? 0 : Math.sin(phase) * 0.55;
  const armSwing = reduced ? 0 : Math.sin(phase) * 0.42;
  const hipY = -22;
  const LEAN = 0.1; // hikers lean slightly into the grade, not upright

  // ground shadow — widens/fades as the figure bobs up (foot leaving the line)
  ctx.beginPath();
  ctx.ellipse(0, 0.8, 8 - bob * 0.6, 1.8, 0, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(43,42,38,${0.18 - bob * 0.02})`;
  ctx.fill();

  const drawLeg = (swing, thighColor, shinColor) => {
    const thighAngle = swing * 0.85;
    const kneeX = Math.sin(thighAngle) * 11;
    const kneeY = hipY + Math.cos(thighAngle) * 11;
    // knee bend: leading/trailing legs both flex at the knee through the
    // swing phase (never a rigid straight line hip-to-foot)
    const bend = Math.max(0, Math.cos(thighAngle)) * 0.55 + Math.max(0, -swing) * 0.4;
    const shinAngle = thighAngle + bend;
    const footX = kneeX + Math.sin(shinAngle) * 10;
    const footY = kneeY + Math.cos(shinAngle) * 10;
    limb(ctx, 0, hipY, kneeX, kneeY, 4.4, 3.2, thighColor);
    limb(ctx, kneeX, kneeY, footX, footY, 3.0, 1.9, shinColor);
    // foot: short flat wedge angled with the shin, planted on ground contact
    const footAngle = Math.atan2(footX - kneeX, footY - kneeY);
    const heelX = footX - Math.cos(footAngle) * 1.2;
    const heelY = footY + Math.sin(footAngle) * 1.2;
    const toeX = footX + Math.sin(footAngle) * 4.6;
    const toeY = footY + Math.cos(footAngle) * 2.6;
    ctx.beginPath();
    ctx.moveTo(heelX, heelY);
    ctx.lineTo(toeX, toeY);
    ctx.lineTo(footX, footY + 1.6);
    ctx.closePath();
    ctx.fillStyle = "#2B2A26";
    ctx.fill();
  };

  const drawArm = (sx, sy, swing, upperColor, foreColor) => {
    const elbowX = sx + Math.sin(swing) * 7.5;
    const elbowY = sy + Math.cos(swing) * 7.5;
    const foreSwing = swing * 0.7 + 0.25; // slight natural elbow bend
    const handX = elbowX + Math.sin(foreSwing) * 6.5;
    const handY = elbowY + Math.cos(foreSwing) * 6.5;
    limb(ctx, sx, sy, elbowX, elbowY, 2.6, 2.0, upperColor);
    limb(ctx, elbowX, elbowY, handX, handY, 2.0, 1.5, foreColor);
    return { handX, handY };
  };

  // trailing leg (back), then leading leg (front) — tapered denim tone
  drawLeg(-legSwing, "#4A5568", "#5B6472");
  drawLeg(legSwing, "#3D4759", "#4A5568");

  // Torso, head, arms, backpack all pivot slightly forward from the hip —
  // the lean that separates a walking hiker from a standing mannequin.
  ctx.save();
  ctx.translate(0, hipY);
  ctx.rotate(LEAN);
  ctx.translate(0, -hipY);

  // backpack (behind torso) + bedroll
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(-12.5, hipY - 19, 6.5, 15.5, [3, 2, 2, 3]);
  else ctx.rect(-12.5, hipY - 19, 6.5, 15.5);
  ctx.fillStyle = "#8B6F47";
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect ? ctx.roundRect(-11.5, hipY - 16.5, 4.5, 5, 1) : ctx.rect(-11.5, hipY - 16.5, 4.5, 5);
  ctx.fillStyle = "#7A5F3D";
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-9.2, hipY - 20.5, 4.4, 2.4, -0.08, 0, Math.PI * 2);
  ctx.fillStyle = "#A13F2C";
  ctx.fill();

  // trailing arm (swings back), then jacket torso, then leading arm + pole
  const shoulderY = hipY - 18.5;
  drawArm(3.2, shoulderY, armSwing, "#24382f", "#1c2c24");

  ctx.beginPath();
  ctx.moveTo(-5.5, hipY);
  ctx.quadraticCurveTo(-7.5, hipY - 14, -4.5, hipY - 20.5);
  ctx.quadraticCurveTo(0, hipY - 22.5, 4.5, hipY - 20.5);
  ctx.quadraticCurveTo(7.5, hipY - 14, 5.5, hipY);
  ctx.closePath();
  const jacketGrad = ctx.createLinearGradient(-6, hipY - 20, 6, hipY);
  jacketGrad.addColorStop(0, "#345c4c");
  jacketGrad.addColorStop(1, "#2a4738");
  ctx.fillStyle = jacketGrad;
  ctx.fill();
  // zipper/seam line for a little surface detail at this scale
  ctx.beginPath();
  ctx.moveTo(0, hipY - 21.5);
  ctx.lineTo(0, hipY - 1);
  ctx.strokeStyle = "rgba(20,32,26,0.5)";
  ctx.lineWidth = 0.6;
  ctx.stroke();

  const { handX: leadHandX, handY: leadHandY } = drawArm(
    3.2, shoulderY, -armSwing * 0.6 + 0.3, "#2F4A3E", "#24382f"
  );

  // trekking pole, planted opposite the leading foot, gripped by the
  // leading-arm hand rather than a fixed shoulder point
  const poleSide = legSwing >= 0 ? -1 : 1;
  ctx.beginPath();
  ctx.moveTo(leadHandX, leadHandY);
  ctx.lineTo(leadHandX + poleSide * 8, 1.5);
  ctx.strokeStyle = "#3a3226";
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(leadHandX, leadHandY, 1, 0, Math.PI * 2);
  ctx.fillStyle = "#3a3226";
  ctx.fill();

  // neck + head + brimmed hat
  const headY = hipY - 25.5;
  ctx.beginPath();
  ctx.moveTo(-1.3, hipY - 21.5);
  ctx.lineTo(1.3, hipY - 21.5);
  ctx.lineTo(1, headY + 2);
  ctx.lineTo(-1, headY + 2);
  ctx.closePath();
  ctx.fillStyle = "#C9A182";
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(0.4, headY, 3.1, 3.4, 0, 0, Math.PI * 2);
  ctx.fillStyle = "#D9B99B";
  ctx.fill();
  // jaw shadow for a touch of depth
  ctx.beginPath();
  ctx.ellipse(1.2, headY + 1.4, 2, 1.6, 0, 0, Math.PI);
  ctx.fillStyle = "rgba(180,140,110,0.35)";
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(0.4, headY - 1.6, 4.6, 1.5, -0.05, 0, Math.PI * 2);
  ctx.fillStyle = "#2B2A26";
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-0.2, headY - 3.1, 2.7, 2.1, 0, Math.PI, Math.PI * 2);
  ctx.fillStyle = "#33322c";
  ctx.fill();

  ctx.restore(); // end forward-lean group
  ctx.restore();
}

export default function RouteHiker({ path, size = 40, speed = 34 }) {
  const reduce = useReducedMotion();
  const outerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const startPct = path[0];

  useEffect(() => {
    const canvas = canvasRef.current;
    const outer = outerRef.current;
    if (!canvas || !outer) return;
    const ctx = canvas.getContext("2d");
    const w = size * 0.62;
    const h = size;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(w / 2, h);

    if (reduce) {
      // Static pose, no loop, no pacing — just stand at the marker.
      drawHiker(ctx, h, { phase: 0, reduced: true });
      return;
    }

    const N = path.length;
    // ~px/sec target, converted to samples/sec assuming roughly-even
    // catmull-rom sampling along the route.
    const samplesPerSec = speed / 8;
    let progress = 0; // 0..N-1
    let dir = 1;
    let phase = 0;
    let last = null;
    const strideRate = 3.4; // radians/sec

    const positionAt = (p) => {
      const i0 = Math.max(0, Math.min(N - 1, Math.floor(p)));
      const i1 = Math.min(N - 1, i0 + 1);
      const frac = p - i0;
      const a = path[i0], b = path[i1];
      return {
        xPct: a.xPct + (b.xPct - a.xPct) * frac,
        yPct: a.yPct + (b.yPct - a.yPct) * frac,
      };
    };

    const tick = (t) => {
      if (last == null) last = t;
      const dt = Math.min((t - last) / 1000, 0.05);
      last = t;

      progress += dir * samplesPerSec * dt;
      if (progress > N - 1) {
        progress = N - 1;
        dir = -1;
      } else if (progress < 0) {
        progress = 0;
        dir = 1;
      }
      phase += strideRate * dt;

      const { xPct, yPct } = positionAt(progress);
      outer.style.left = `${xPct}%`;
      outer.style.top = `${yPct}%`;
      outer.style.setProperty("--hiker-flip", dir < 0 ? "-1" : "1");

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(-w, -h, w * 2, h * 2);
      ctx.translate(w / 2, h);
      drawHiker(ctx, h, { phase, reduced: false });

      rafRef.current = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && rafRef.current == null) {
          last = null;
          rafRef.current = requestAnimationFrame(tick);
        } else if (!entry.isIntersecting && rafRef.current != null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      },
      { threshold: 0.05 }
    );
    io.observe(outer);

    return () => {
      io.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [reduce, size, speed, path]);

  // Two independent transform owners so they never fight over the same
  // element's inline style: the outer motion.div handles the one-time
  // entrance slide (framer-motion writes its own transform via x/opacity)
  // plus the ongoing walk position (rAF loop imperatively sets left/top —
  // untouched by framer-motion, so no conflict); the inner div only
  // handles the flip, via a plain CSS var.
  return (
    <motion.div
      ref={outerRef}
      data-testid="route-hiker"
      aria-hidden="true"
      initial={reduce ? { opacity: 0 } : { opacity: 0, x: -Math.max(60, size * 2) }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: reduce ? 0.25 : 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="pointer-events-none absolute"
      style={{ left: `${startPct.xPct}%`, top: `${startPct.yPct}%` }}
    >
      <div style={{ transform: "translate(-50%, -100%) scaleX(var(--hiker-flip, 1))" }}>
        <canvas ref={canvasRef} />
      </div>
    </motion.div>
  );
}
